import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, User, ArrowUpRight, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, formatApiError, fmtDate } from "@/lib/api";
import TableToolbar, { useVisibleColumns } from "@/components/TableToolbar";

const ALL_COLUMNS = [
  { key: "name", label: "Name", alwaysOn: true },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "vehicles", label: "Vehicles" },
  { key: "since", label: "Since" },
];

const VIEWS = [
  { key: "all", label: "All customers" },
  { key: "recent", label: "New this month" },
  { key: "no_email", label: "Missing email" },
];

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [view, setView] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", notes: "" });
  const [visibleCols, toggleCol] = useVisibleColumns("customers", ALL_COLUMNS);

  const load = (query = "") => {
    api.get("/customers", { params: query ? { q: query } : {} }).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, []);

  // Auto-open the Add-Customer dialog when the Quick-Create menu deep-links
  // us here with `?new=1`. Strip the flag so refresh doesn't retrigger.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("new") === "1") {
      setOpen(true);
      params.delete("new");
      const cleaned = params.toString() ? `?${params.toString()}` : "";
      window.history.replaceState({}, "", window.location.pathname + cleaned);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/customers", form);
      toast.success("Customer added");
      setOpen(false);
      setForm({ name: "", phone: "", email: "", address: "", notes: "" });
      load(q);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = useMemo(() => {
    if (view === "recent") {
      const cutoff = new Date();
      cutoff.setMonth(cutoff.getMonth() - 1);
      return items.filter((c) => new Date(c.created_at) >= cutoff);
    }
    if (view === "no_email") return items.filter((c) => !c.email);
    return items;
  }, [items, view]);

  const rows = q
    ? filtered.filter((c) =>
        [c.name, c.phone, c.email].filter(Boolean).some((v) =>
          String(v).toLowerCase().includes(q.toLowerCase())
        )
      )
    : filtered;

  const isVisible = (k) => ALL_COLUMNS.find((c) => c.key === k)?.alwaysOn || visibleCols.has(k);

  return (
    <div className="space-y-5" data-testid="customers-root">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Directory</div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557]">Customers</h1>
          <p className="text-[13px] text-[#667085] mt-0.5">Every person who trusts you with a vehicle.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="customers-add-btn" className="h-10 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold text-[13px] px-4">
              <Plus size={15} className="mr-1.5" /> New customer
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-lg border-[#E4E7EC] max-w-md">
            <DialogHeader><DialogTitle className="font-semibold text-[#1d3557]">Add customer</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="customer-form">
              <div>
                <Label className="text-[12px] font-semibold text-[#1d3557]">Name</Label>
                <Input data-testid="customer-name-input" required className="rounded-md mt-1" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[12px] font-semibold text-[#1d3557]">Phone</Label>
                  <Input data-testid="customer-phone-input" required className="rounded-md mt-1" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[12px] font-semibold text-[#1d3557]">Email</Label>
                  <Input data-testid="customer-email-input" type="email" className="rounded-md mt-1" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-[12px] font-semibold text-[#1d3557]">Address</Label>
                <Textarea data-testid="customer-address-input" className="rounded-md mt-1" rows={2} value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div>
                <Label className="text-[12px] font-semibold text-[#1d3557]">Notes</Label>
                <Textarea data-testid="customer-notes-input" className="rounded-md mt-1" rows={2} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <Button data-testid="customer-save-btn" type="submit" className="w-full h-11 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold">
                Save customer
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <TableToolbar
        testIdPrefix="customers"
        searchValue={q}
        onSearchChange={setQ}
        searchPlaceholder="Search name, phone or email…"
        views={VIEWS}
        activeView={view}
        onViewChange={setView}
        columns={ALL_COLUMNS}
        visibleColumns={visibleCols}
        onToggleColumn={toggleCol}
      />

      {/* Desktop */}
      <div className="hidden md:block bg-white border border-[#E4E7EC] rounded-lg overflow-hidden" data-testid="customers-table">
        <table className="w-full text-[13px]">
          <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Name</th>
              {isVisible("phone") && <th className="text-left px-4 py-3 font-semibold">Phone</th>}
              {isVisible("email") && <th className="text-left px-4 py-3 font-semibold">Email</th>}
              {isVisible("vehicles") && <th className="text-left px-4 py-3 font-semibold">Vehicles</th>}
              {isVisible("since") && <th className="text-left px-4 py-3 font-semibold">Since</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-[13px] text-[#667085]">
                  No customers match this view.
                </td>
              </tr>
            )}
            {rows.map((c) => (
              <tr key={c.id} className="hover:bg-[#F7F9FC] transition-colors" data-testid={`customer-row-${c.id}`}>
                <td className="px-4 py-3">
                  <Link
                    to={`/customers/${c.id}`}
                    data-testid={`customer-link-${c.id}`}
                    className="inline-flex items-center gap-2 text-[#1d3557] hover:text-[#e63946] font-medium"
                  >
                    <User size={14} className="text-[#457b9d]" /> {c.name}
                  </Link>
                </td>
                {isVisible("phone") && <td className="px-4 py-3 font-mono text-[12px] text-[#457b9d]">{c.phone}</td>}
                {isVisible("email") && <td className="px-4 py-3 text-[12px] text-[#667085]">{c.email || "—"}</td>}
                {isVisible("vehicles") && <td className="px-4 py-3 text-[12px] text-[#667085]">{c.vehicle_count ?? "—"}</td>}
                {isVisible("since") && <td className="px-4 py-3 text-[11px] text-[#667085] font-mono">{fmtDate(c.created_at)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2.5" data-testid="customers-cards">
        {rows.length === 0 && (
          <div className="bg-white rounded-lg border border-[#E4E7EC] p-8 text-center text-[13px] text-[#667085]">
            No customers match this view.
          </div>
        )}
        {rows.map((c) => (
          <Link
            key={c.id}
            to={`/customers/${c.id}`}
            data-testid={`customer-card-${c.id}`}
            className="block bg-white rounded-lg border border-[#E4E7EC] p-3.5 active:bg-[#F7F9FC]"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#F1FAEE] grid place-items-center text-[#457b9d] shrink-0">
                <User size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold text-[#1d3557] truncate flex items-center gap-1">
                  {c.name} <ArrowUpRight size={12} className="text-[#a8dadc]" />
                </div>
                <div className="flex items-center gap-3 text-[11px] text-[#667085] mt-0.5">
                  {c.phone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {c.phone}</span>}
                  {c.email && <span className="inline-flex items-center gap-1 truncate"><Mail size={10} /> {c.email}</span>}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
